export interface TenantConfig {
  apiBaseUrl: string
  authProvider: 'dev' | 'clerk'
  displayName: string
  clerkPublishableKey: string
}

/** Known host → deployment defaults when Vite env vars are missing at build time. */
const HOST_DEFAULTS: Record<string, Partial<TenantConfig>> = {
  'bt.toolroan.com': {
    apiBaseUrl: 'https://api-bt.toolroan.com',
    authProvider: 'clerk',
    displayName: 'BetTekka',
    // Clerk publishable keys are public client-side identifiers (not secrets).
    // Baked in so a build missing VITE_CLERK_PUBLISHABLE_KEY doesn't white-screen.
    clerkPublishableKey:
      'pk_test_Y2xvc2luZy10cmVlZnJvZy02Mi5jbGVyay5hY2NvdW50cy5kZXYk',
  },
  'demo.toolroan.com': {
    apiBaseUrl: 'https://api-demo.toolroan.com',
    authProvider: 'dev',
    displayName: 'Demo',
  },
  'live.toolroan.com': {
    apiBaseUrl: 'https://api-live.toolroan.com',
    authProvider: 'clerk',
    displayName: 'Live',
    // Same Clerk test instance as bt (public client-side key).
    clerkPublishableKey:
      'pk_test_Y2xvc2luZy10cmVlZnJvZy02Mi5jbGVyay5hY2NvdW50cy5kZXYk',
  },
  'localhost': {
    apiBaseUrl: 'http://localhost:8080',
    authProvider: 'dev',
    displayName: 'Local',
  },
}

function hostDefaults(): Partial<TenantConfig> {
  if (typeof window === 'undefined') return {}
  return HOST_DEFAULTS[window.location.hostname] ?? {}
}

/**
 * Resolve runtime tenant configuration.
 *
 * Env vars from Vite build take precedence; hostname defaults fill gaps so
 * Cloudflare Pages deploys survive missing build-time env injection.
 */
export function resolveTenantConfig(): TenantConfig {
  const defaults = hostDefaults()

  const apiBaseUrl =
    (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ||
    defaults.apiBaseUrl ||
    ''
  let authProvider = (
    (import.meta.env.VITE_AUTH_PROVIDER as string | undefined)?.trim() ||
    defaults.authProvider ||
    'dev'
  ) as TenantConfig['authProvider']
  const displayName =
    (import.meta.env.VITE_DISPLAY_NAME as string | undefined)?.trim() ||
    defaults.displayName ||
    'Attribution'
  const clerkPublishableKey =
    (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined)?.trim() ||
    defaults.clerkPublishableKey ||
    ''

  // Selecting Clerk without a publishable key makes ClerkProvider throw on
  // mount and white-screens the whole app. Fall back to dev auth so the
  // dashboard still renders.
  if (authProvider === 'clerk' && !clerkPublishableKey) {
    console.error(
      'Clerk auth selected but VITE_CLERK_PUBLISHABLE_KEY is missing — falling back to dev auth. Rebuild with the publishable key for production login.',
    )
    authProvider = 'dev'
  }

  if (!apiBaseUrl) {
    console.error(
      'API base URL is not configured — set VITE_API_BASE_URL at build time or add a hostname default in tenant.ts',
    )
  }

  return { apiBaseUrl, authProvider, displayName, clerkPublishableKey }
}

export const tenantConfig = resolveTenantConfig()
